import { integrationHealthService } from "./integration-health.service";
import type {
  IntegrationType,
  HealthIssue,
  ConnectionHealth,
} from "./integration-health.service";

type AlertHealth = ConnectionHealth & {
  isConnected?: boolean;
  consecutiveFailures?: number;
};

interface WindowWithAlertService extends Window {
  alertService?: AlertService;
}

interface AlertMetadata {
  healthScore?: number;
  dataQuality?: number;
  lastSync?: string;
  issueCount?: number;
}

export interface Alert {
  id: string;
  title: string;
  message: string;
  severity: "critical" | "warning" | "info";
  category: "connection" | "data" | "performance" | "sync" | "security";
  integrationType: IntegrationType;
  integrationName: string;
  timestamp: string;
  isRead: boolean;
  isResolved: boolean;
  autoResolvable: boolean;
  actions: AlertAction[];
  metadata?: AlertMetadata;
  groupKey?: string; // Para agrupar alertas similares
}

interface AlertAction {
  id: string;
  label: string;
  action: "retry" | "sync" | "reconnect" | "ignore" | "configure" | "external";
  url?: string;
  confirmMessage?: string;
}

interface AlertRule {
  id: string;
  name: string;
  description: string;
  condition: (health: AlertHealth) => boolean;
  severity: Alert["severity"];
  category: Alert["category"];
  cooldownMs: number; // Tempo mínimo entre alertas similares
  autoResolve: boolean;
  actions: AlertAction[];
}

// Regras predefinidas de alertas
const ALERT_RULES: AlertRule[] = [
  {
    id: "connection-lost",
    name: "Conexão Perdida",
    description: "Integração perdeu conexão",
    condition: (health) => !health.isConnected,
    severity: "critical",
    category: "connection",
    cooldownMs: 5 * 60 * 1000, // 5 minutos
    autoResolve: true,
    actions: [
      { id: "reconnect", label: "Reconectar", action: "reconnect" },
      { id: "configure", label: "Configurar", action: "configure" },
    ],
  },
  {
    id: "low-health-score",
    name: "Score de Saúde Baixo",
    description: "Score de saúde abaixo de 60",
    condition: (health) => health.score < 60,
    severity: "warning",
    category: "performance",
    cooldownMs: 30 * 60 * 1000, // 30 minutos
    autoResolve: true,
    actions: [
      { id: "sync", label: "Sincronizar", action: "sync" },
      {
        id: "check-issues",
        label: "Ver Problemas",
        action: "external",
        url: "/dashboard/health",
      },
    ],
  },
  {
    id: "critical-data-quality",
    name: "Qualidade de Dados Crítica",
    description: "Qualidade dos dados abaixo de 50%",
    condition: (health) => (health.dataQuality ?? 100) < 50,
    severity: "critical",
    category: "data",
    cooldownMs: 15 * 60 * 1000, // 15 minutos
    autoResolve: false,
    actions: [
      { id: "sync", label: "Sincronizar Dados", action: "sync" },
      {
        id: "investigate",
        label: "Investigar",
        action: "external",
        url: "/dashboard/health",
      },
    ],
  },
  {
    id: "sync-failure",
    name: "Falha na Sincronização",
    description: "Múltiplas falhas consecutivas na sincronização",
    condition: (health) => (health.consecutiveFailures ?? 0) >= 3,
    severity: "critical",
    category: "sync",
    cooldownMs: 10 * 60 * 1000, // 10 minutos
    autoResolve: true,
    actions: [
      { id: "retry", label: "Tentar Novamente", action: "retry" },
      { id: "reconnect", label: "Reconectar", action: "reconnect" },
    ],
  },
  {
    id: "stale-data",
    name: "Dados Desatualizados",
    description: "Dados não atualizados há mais de 24 horas",
    condition: (health) => {
      const lastSync = new Date(health.lastSync || 0);
      const hoursAgo = (Date.now() - lastSync.getTime()) / (1000 * 60 * 60);
      return hoursAgo > 24;
    },
    severity: "warning",
    category: "sync",
    cooldownMs: 6 * 60 * 60 * 1000, // 6 horas
    autoResolve: true,
    actions: [{ id: "sync", label: "Sincronizar Agora", action: "sync" }],
  },
  {
    id: "rate-limit-hit",
    name: "Limite de Taxa Atingido",
    description: "API atingiu limite de requisições",
    condition: (health) =>
      health.issues?.some(
        (issue: HealthIssue) =>
          issue.message.toLowerCase().includes("rate limit") ||
          issue.message.toLowerCase().includes("quota exceeded"),
      ),
    severity: "warning",
    category: "performance",
    cooldownMs: 60 * 60 * 1000, // 1 hora
    autoResolve: true,
    actions: [
      { id: "wait", label: "Aguardar Reset", action: "ignore" },
      { id: "configure", label: "Configurar Limites", action: "configure" },
    ],
  },
];

class AlertService {
  private alerts: Alert[] = [];
  private alertHistory: Alert[] = [];
  private listeners: Set<(alerts: Alert[]) => void> = new Set();
  private cooldowns: Map<string, number> = new Map();
  private monitoringInterval?: NodeJS.Timeout;

  constructor() {
    this.loadAlertsFromStorage();
    this.startMonitoring();
  }

  /**
   * Inicia o monitoramento automático de alertas
   */
  startMonitoring(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
    }

    this.monitoringInterval = setInterval(() => {
      this.checkForAlerts();
    }, 30000); // Verificar a cada 30 segundos

    console.log("🔔 Sistema de alertas iniciado");
  }

  /**
   * Para o monitoramento
   */
  stopMonitoring(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = undefined;
    }
  }

  /**
   * Verifica todas as integrações e gera alertas baseado nas regras
   */
  private async checkForAlerts(): Promise<void> {
    try {
      const healthData = integrationHealthService.getAllHealth();

      for (const [integrationId, health] of healthData) {
        const integrationType = integrationId.split("-")[0] as IntegrationType;
        await this.evaluateRules(integrationType, integrationId, health);
      }

      this.cleanupResolvedAlerts();
      this.saveAlertsToStorage();
      this.notifyListeners();
    } catch (error) {
      console.error("Erro ao verificar alertas:", error);
    }
  }

  /**
   * Avalia regras de alerta para uma integração específica
   */
  private async evaluateRules(
    integrationType: IntegrationType,
    integrationId: string,
    health: AlertHealth,
  ): Promise<void> {
    for (const rule of ALERT_RULES) {
      try {
        if (rule.condition(health)) {
          await this.createAlert(rule, integrationType, integrationId, health);
        } else if (rule.autoResolve) {
          this.resolveAlerts(rule.id, integrationId);
        }
      } catch (error) {
        console.error(`Erro ao avaliar regra ${rule.id}:`, error);
      }
    }
  }

  /**
   * Cria um novo alerta se não estiver em cooldown
   */
  private async createAlert(
    rule: AlertRule,
    integrationType: IntegrationType,
    integrationId: string,
    health: AlertHealth,
  ): Promise<void> {
    const cooldownKey = `${rule.id}-${integrationId}`;
    const now = Date.now();

    // Verificar cooldown
    const lastAlert = this.cooldowns.get(cooldownKey);
    if (lastAlert && now - lastAlert < rule.cooldownMs) {
      return;
    }

    // Verificar se já existe alerta similar não resolvido
    const existingAlert = this.alerts.find(
      (alert) => alert.groupKey === cooldownKey && !alert.isResolved,
    );

    if (existingAlert) {
      // Atualizar timestamp do alerta existente
      existingAlert.timestamp = new Date().toISOString();
      return;
    }

    const integrationName = this.getIntegrationName(integrationType);

    const alert: Alert = {
      id: `${rule.id}-${integrationId}-${now}`,
      title: rule.name,
      message: `${integrationName}: ${rule.description}`,
      severity: rule.severity,
      category: rule.category,
      integrationType,
      integrationName,
      timestamp: new Date().toISOString(),
      isRead: false,
      isResolved: false,
      autoResolvable: rule.autoResolve,
      actions: rule.actions,
      groupKey: cooldownKey,
      metadata: {
        healthScore: health.score,
        dataQuality: health.dataQuality,
        lastSync: health.lastSync,
        issueCount: health.issues?.length || 0,
      },
    };

    this.alerts.unshift(alert); // Adicionar no início
    this.cooldowns.set(cooldownKey, now);

    console.log(
      `🔔 Novo alerta criado: ${alert.title} para ${integrationName}`,
    );

    // Notificar imediatamente para alertas críticos
    if (alert.severity === "critical") {
      this.showNotification(alert);
    }
  }

  /**
   * Resolve alertas baseado na regra e integração
   */
  private resolveAlerts(ruleId: string, integrationId: string): void {
    const groupKey = `${ruleId}-${integrationId}`;

    this.alerts
      .filter((alert) => alert.groupKey === groupKey && !alert.isResolved)
      .forEach((alert) => {
        alert.isResolved = true;
        console.log(`✅ Alerta resolvido automaticamente: ${alert.title}`);
      });
  }

  /**
   * Remove alertas resolvidos antigos (mais de 24 horas)
   */
  private cleanupResolvedAlerts(): void {
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;

    const activeAlerts = this.alerts.filter((alert) => {
      if (alert.isResolved && new Date(alert.timestamp).getTime() < oneDayAgo) {
        this.alertHistory.push(alert);
        return false;
      }
      return true;
    });

    this.alerts = activeAlerts;

    // Manter apenas últimos 100 alertas no histórico
    this.alertHistory = this.alertHistory.slice(-100);
  }

  /**
   * Marca um alerta como lido
   */
  markAsRead(alertId: string): void {
    const alert = this.alerts.find((a) => a.id === alertId);
    if (alert) {
      alert.isRead = true;
      this.saveAlertsToStorage();
      this.notifyListeners();
    }
  }

  /**
   * Marca um alerta como resolvido
   */
  markAsResolved(alertId: string): void {
    const alert = this.alerts.find((a) => a.id === alertId);
    if (alert) {
      alert.isResolved = true;
      this.saveAlertsToStorage();
      this.notifyListeners();
    }
  }

  /**
   * Executa uma ação de alerta
   */
  async executeAction(alertId: string, actionId: string): Promise<void> {
    const alert = this.alerts.find((a) => a.id === alertId);
    if (!alert) return;

    const action = alert.actions.find((a) => a.id === actionId);
    if (!action) return;

    try {
      switch (action.action) {
        case "retry":
        case "sync":
          await integrationHealthService.forceHealthCheck(
            alert.integrationType,
            alert.integrationType + "-main",
          );
          break;

        case "reconnect":
          // Implementar lógica de reconexão
          console.log(`Tentando reconectar ${alert.integrationName}`);
          break;

        case "ignore":
          this.markAsResolved(alertId);
          break;

        case "external":
          if (action.url) {
            window.open(action.url, "_blank");
          }
          break;
      }

      console.log(`🎯 Ação executada: ${action.label} para ${alert.title}`);
    } catch (error) {
      console.error(`Erro ao executar ação ${action.label}:`, error);
    }
  }

  /**
   * Obtém todos os alertas ativos
   */
  getAlerts(): Alert[] {
    return [...this.alerts];
  }

  /**
   * Obtém alertas não lidos
   */
  getUnreadAlerts(): Alert[] {
    return this.alerts.filter((alert) => !alert.isRead && !alert.isResolved);
  }

  /**
   * Obtém alertas críticos
   */
  getCriticalAlerts(): Alert[] {
    return this.alerts.filter(
      (alert) => alert.severity === "critical" && !alert.isResolved,
    );
  }

  /**
   * Obtém estatísticas de alertas
   */
  getStats(): {
    total: number;
    unread: number;
    critical: number;
    byCategory: Record<string, number>;
    bySeverity: Record<string, number>;
  } {
    const activeAlerts = this.alerts.filter((a) => !a.isResolved);

    return {
      total: activeAlerts.length,
      unread: this.getUnreadAlerts().length,
      critical: this.getCriticalAlerts().length,
      byCategory: activeAlerts.reduce(
        (acc, alert) => {
          acc[alert.category] = (acc[alert.category] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      ),
      bySeverity: activeAlerts.reduce(
        (acc, alert) => {
          acc[alert.severity] = (acc[alert.severity] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      ),
    };
  }

  /**
   * Adiciona um listener para mudanças de alertas
   */
  addListener(callback: (alerts: Alert[]) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notifica todos os listeners
   */
  private notifyListeners(): void {
    this.listeners.forEach((callback) => {
      try {
        callback([...this.alerts]);
      } catch (error) {
        console.error("Erro ao notificar listener de alertas:", error);
      }
    });
  }

  /**
   * Mostra notificação nativa do browser
   */
  private showNotification(alert: Alert): void {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(`${alert.integrationName}: ${alert.title}`, {
        body: alert.message,
        icon: "/favicon.ico",
        tag: alert.id,
        requireInteraction: alert.severity === "critical",
      });
    }
  }

  /**
   * Solicita permissão para notificações
   */
  async requestNotificationPermission(): Promise<boolean> {
    if ("Notification" in window) {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    }
    return false;
  }

  /**
   * Salva alertas no localStorage
   */
  private saveAlertsToStorage(): void {
    try {
      localStorage.setItem(
        "health_alerts",
        JSON.stringify({
          alerts: this.alerts,
          history: this.alertHistory.slice(-50), // Salvar apenas últimos 50
          timestamp: Date.now(),
        }),
      );
    } catch (error) {
      console.error("Erro ao salvar alertas:", error);
    }
  }

  /**
   * Carrega alertas do localStorage
   */
  private loadAlertsFromStorage(): void {
    try {
      const data = localStorage.getItem("health_alerts");
      if (data) {
        const parsed = JSON.parse(data);

        // Carregar apenas alertas recentes (últimas 24 horas)
        const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;

        this.alerts = (parsed.alerts || []).filter(
          (alert: Alert) => new Date(alert.timestamp).getTime() > oneDayAgo,
        );

        this.alertHistory = parsed.history || [];

        console.log(`📁 ${this.alerts.length} alertas carregados do storage`);
      }
    } catch (error) {
      console.error("Erro ao carregar alertas:", error);
      this.alerts = [];
    }
  }

  /**
   * Obtém nome amigável da integração
   */
  private getIntegrationName(type: IntegrationType): string {
    const names: Record<IntegrationType, string> = {
      google_ads: "Google Ads",
      google_analytics: "Google Analytics",
      meta_ads: "Meta Ads",
      search_console: "Search Console",
      instagram_insights: "Instagram Insights",
    };

    return names[type] || type;
  }

  /**
   * Limpa todos os alertas (para desenvolvimento)
   */
  clearAll(): void {
    this.alerts = [];
    this.alertHistory = [];
    this.cooldowns.clear();
    localStorage.removeItem("health_alerts");
    this.notifyListeners();
  }
}

// Instância singleton
export const alertService = new AlertService();

// Para desenvolvimento - expor no console
if (typeof window !== "undefined") {
  (window as WindowWithAlertService).alertService = alertService;
}
